import _toConsumableArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getClient } from './currentScopes.js';
import { SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN } from './semanticAttributes.js';
import { getActiveSpan } from './utils/spanUtils.js';
import { setHttpStatus, SPAN_STATUS_ERROR } from './tracing/spanstatus.js';
import { isRequest, isInstanceOf } from './utils/is.js';
import { hasSpansEnabled } from './utils/hasSpansEnabled.js';
import { SENTRY_BAGGAGE_KEY_PREFIX } from './utils/baggage.js';
import { SentryNonRecordingSpan } from './tracing/sentryNonRecordingSpan.js';
import { startInactiveSpan } from './tracing/trace.js';
import { getTraceData } from './utils/traceData.js';
import { parseStringToURLObject, getSanitizedUrlStringFromUrlObject, isURLObjectRelative } from './utils/url.js';

/**
 * Create and track fetch request spans for usage in combination with `addFetchInstrumentationHandler`.
 *
 * @returns Span if a span was created, otherwise void.
 */
function instrumentFetchRequest(handlerData, shouldCreateSpan, shouldAttachHeaders, spans) {
  var spanOrigin = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 'auto.http.browser';
  if (!handlerData.fetchData) {
    return undefined;
  }
  var _handlerData$fetchDat = handlerData.fetchData,
    method = _handlerData$fetchDat.method,
    url = _handlerData$fetchDat.url;
  var shouldCreateSpanResult = hasSpansEnabled() && shouldCreateSpan(url);
  if (handlerData.endTimestamp && shouldCreateSpanResult) {
    var spanId = handlerData.fetchData.__span;
    if (!spanId) return;
    var _span = spans[spanId];
    if (_span) {
      endSpan(_span, handlerData);

      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete spans[spanId];
    }
    return undefined;
  }
  var hasParent = !!getActiveSpan();
  var span = shouldCreateSpanResult && hasParent ? startInactiveSpan(getSpanStartOptions(url, method, spanOrigin)) : new SentryNonRecordingSpan();
  handlerData.fetchData.__span = span.spanContext().spanId;
  spans[span.spanContext().spanId] = span;
  if (shouldAttachHeaders(handlerData.fetchData.url)) {
    var request = handlerData.args[0];
    var options = handlerData.args[1] || {};
    var headers = _addTracingHeadersToFetchRequest(request, options,
    // If performance is disabled (TWP) or there's no active root span (pageload/navigation/interaction),
    // we do not want to use the span as base for the trace headers,
    // which means that the headers will be generated from the scope and the sampling decision is deferred
    hasSpansEnabled() && hasParent ? span : undefined);
    if (headers) {
      // Ensure this is actually set, if no options have been passed previously
      handlerData.args[1] = options;
      options.headers = headers;
    }
  }
  var client = getClient();
  if (client) {
    var fetchHint = {
      input: handlerData.args,
      response: handlerData.response,
      startTimestamp: handlerData.startTimestamp,
      endTimestamp: handlerData.endTimestamp
    };
    client.emit('beforeOutgoingRequestSpan', span, fetchHint);
  }
  return span;
}

/**
 * Adds sentry-trace and baggage headers to the various forms of fetch headers.
 * exported only for testing purposes
 *
 * When we determine if we should add a baggage header, there are 3 cases:
 * 1. No previous baggage header -> add baggage
 * 2. Previous baggage header has no sentry baggage values -> add our baggage
 * 3. Previous baggage header has sentry baggage values -> do nothing (might have been added manually by users)
 */
// eslint-disable-next-line complexity -- yup it's this complicated :(
function _addTracingHeadersToFetchRequest(request, fetchOptionsObj, span) {
  var traceHeaders = getTraceData({
    span: span
  });
  var sentryTrace = traceHeaders['sentry-trace'];
  var baggage = traceHeaders.baggage;

  // Nothing to do, when we return undefined here, the original headers will be used
  if (!sentryTrace) {
    return undefined;
  }
  var originalHeaders = fetchOptionsObj.headers || (isRequest(request) ? request.headers : undefined);
  if (!originalHeaders) {
    return _objectSpread({}, traceHeaders);
  } else if (isHeaders(originalHeaders)) {
    var newHeaders = new Headers(originalHeaders);

    // We don't want to override manually added sentry headers
    if (!newHeaders.get('sentry-trace')) {
      newHeaders.set('sentry-trace', sentryTrace);
    }
    if (baggage) {
      var prevBaggageHeader = newHeaders.get('baggage');
      if (!prevBaggageHeader) {
        newHeaders.set('baggage', baggage);
      } else if (!baggageHeaderHasSentryBaggageValues(prevBaggageHeader)) {
        newHeaders.set('baggage', "".concat(prevBaggageHeader, ",").concat(baggage));
      }
    }
    return newHeaders;
  } else if (Array.isArray(originalHeaders)) {
    var _newHeaders = _toConsumableArray(originalHeaders);
    if (!originalHeaders.find(function (header) {
      return header[0] === 'sentry-trace';
    })) {
      _newHeaders.push(['sentry-trace', sentryTrace]);
    }
    var prevBaggageHeaderWithSentryValues = originalHeaders.find(function (header) {
      return header[0] === 'baggage' && baggageHeaderHasSentryBaggageValues(header[1]);
    });
    if (baggage && !prevBaggageHeaderWithSentryValues) {
      // If there are multiple entries with the same key, the browser will merge the values into a single request header.
      // Its therefore safe to simply push a "baggage" entry, even though there might already be another baggage header.
      _newHeaders.push(['baggage', baggage]);
    }
    return _newHeaders;
  } else {
    var existingSentryTraceHeader = 'sentry-trace' in originalHeaders ? originalHeaders['sentry-trace'] : undefined;
    var existingBaggageHeader = 'baggage' in originalHeaders ? originalHeaders.baggage : undefined;
    var newBaggageHeaders = existingBaggageHeader ? Array.isArray(existingBaggageHeader) ? _toConsumableArray(existingBaggageHeader) : [existingBaggageHeader] : [];
    var _prevBaggageHeaderWithSentryValues = existingBaggageHeader && (Array.isArray(existingBaggageHeader) ? existingBaggageHeader.find(function (headerItem) {
      return baggageHeaderHasSentryBaggageValues(headerItem);
    }) : baggageHeaderHasSentryBaggageValues(existingBaggageHeader));
    if (baggage && !_prevBaggageHeaderWithSentryValues) {
      newBaggageHeaders.push(baggage);
    }
    return _objectSpread(_objectSpread({}, originalHeaders), {}, {
      'sentry-trace': existingSentryTraceHeader !== null && existingSentryTraceHeader !== void 0 ? existingSentryTraceHeader : sentryTrace,
      baggage: newBaggageHeaders.length > 0 ? newBaggageHeaders.join(',') : undefined
    });
  }
}
function endSpan(span, handlerData) {
  if (handlerData.response) {
    var _handlerData$response;
    setHttpStatus(span, handlerData.response.status);
    var contentLength = (_handlerData$response = handlerData.response) === null || _handlerData$response === void 0 || (_handlerData$response = _handlerData$response.headers) === null || _handlerData$response === void 0 ? void 0 : _handlerData$response.get('content-length');
    if (contentLength) {
      var contentLengthNum = parseInt(contentLength);
      if (contentLengthNum > 0) {
        span.setAttribute('http.response_content_length', contentLengthNum);
      }
    }
  } else if (handlerData.error) {
    span.setStatus({
      code: SPAN_STATUS_ERROR,
      message: 'internal_error'
    });
  }
  span.end();
}
function baggageHeaderHasSentryBaggageValues(baggageHeader) {
  return baggageHeader.split(',').some(function (baggageEntry) {
    return baggageEntry.trim().startsWith(SENTRY_BAGGAGE_KEY_PREFIX);
  });
}
function isHeaders(headers) {
  return typeof Headers !== 'undefined' && isInstanceOf(headers, Headers);
}
function getSpanStartOptions(url, method, spanOrigin) {
  var parsedUrl = parseStringToURLObject(url);
  return {
    name: parsedUrl ? "".concat(method, " ").concat(getSanitizedUrlStringFromUrlObject(parsedUrl)) : method,
    attributes: getFetchSpanAttributes(url, parsedUrl, method, spanOrigin)
  };
}
function getFetchSpanAttributes(url, parsedUrl, method, spanOrigin) {
  var attributes = _defineProperty(_defineProperty({
    url: url,
    type: 'fetch',
    'http.method': method
  }, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, spanOrigin), SEMANTIC_ATTRIBUTE_SENTRY_OP, 'http.client');
  if (parsedUrl) {
    if (!isURLObjectRelative(parsedUrl)) {
      attributes['http.url'] = parsedUrl.href;
      attributes['server.address'] = parsedUrl.host;
    }
    if (parsedUrl.search) {
      attributes['http.query'] = parsedUrl.search;
    }
    if (parsedUrl.hash) {
      attributes['http.fragment'] = parsedUrl.hash;
    }
  }
  return attributes;
}
export { _addTracingHeadersToFetchRequest, instrumentFetchRequest };